import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Users, TrendingUp, Apple, Droplet, 
  Calendar, BarChart3, UserCheck, Building2,
  MessageSquare, ArrowRightLeft, Stethoscope
} from "lucide-react";

const AdminOverview = () => {
  const { stats } = useOutletContext();
  const { token } = useAuth();
  const { t } = useLanguage();

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Engagement Stats */}
      <div>
        <h2 className="font-heading text-xl font-bold text-secondary mb-4">
          {t('admin.todays_engagement')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-6 bg-gradient-to-br from-primary/10 to-orange-50 border border-orange-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('admin.daily_active_users')}</p>
                <p className="text-3xl font-bold text-secondary">{stats.engagement.daily_active_users}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-success/10 to-green-50 border border-green-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-success/20 flex items-center justify-center">
                <Apple className="w-6 h-6 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('admin.meals_analyzed_today')}</p>
                <p className="text-3xl font-bold text-secondary">{stats.engagement.meals_analyzed_today}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-blue-500/10 to-blue-50 border border-blue-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-500/20 flex items-center justify-center">
                <Droplet className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('admin.glucose_logs_today')}</p>
                <p className="text-3xl font-bold text-secondary">{stats.engagement.glucose_logs_today}</p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* B2B vs B2C Breakdown */}
      <div>
        <h2 className="font-heading text-xl font-bold text-secondary mb-4">
          {t('admin.user_segments')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-6 bg-gradient-to-br from-violet-500/10 to-violet-50 border border-violet-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-violet-500/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-violet-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('admin.b2c_users')}</p>
                <p className="text-3xl font-bold text-secondary">{stats.users.b2c || 0}</p>
                <p className="text-xs text-muted-foreground">{t('admin.self_signup_users')}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-indigo-500/10 to-indigo-50 border border-indigo-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-500/20 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-indigo-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('admin.b2b_users')}</p>
                <p className="text-3xl font-bold text-secondary">{stats.users.b2b || 0}</p>
                <p className="text-xs text-muted-foreground">{t('admin.with_dietitian_partner')}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-teal-500/10 to-teal-50 border border-teal-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-teal-500/20 flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-teal-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('admin.with_care_team')}</p>
                <p className="text-3xl font-bold text-secondary">{stats.users.with_care_team || 0}</p>
                <p className="text-xs text-muted-foreground">{t('admin.assigned_to_dietitian')}</p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Marketplace / Lead Funnel */}
      {stats.marketplace && (
        <div>
          <h2 className="font-heading text-xl font-bold text-secondary mb-4">
            {t('admin.find_healthcare_provider')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="p-4 bg-white border border-orange-100 text-center">
              <Stethoscope className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-2xl font-bold text-secondary">{stats.dietitians?.active_listings || 0}</p>
              <p className="text-xs text-muted-foreground">{t('admin.active_listings')}</p>
            </Card>
            
            <Card className="p-4 bg-white border border-orange-100 text-center">
              <MessageSquare className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-secondary">{stats.marketplace.total_leads}</p>
              <p className="text-xs text-muted-foreground">{t('admin.total_inquiries')}</p>
            </Card>
            
            <Card className="p-4 bg-white border border-orange-100 text-center">
              <div className="w-6 h-6 rounded-full bg-yellow-400 mx-auto mb-2 flex items-center justify-center">
                <span className="text-xs font-bold text-white">{stats.marketplace.new_leads}</span>
              </div>
              <p className="text-2xl font-bold text-warning">{stats.marketplace.new_leads}</p>
              <p className="text-xs text-muted-foreground">{t('admin.new_leads')}</p>
            </Card>
            
            <Card className="p-4 bg-white border border-orange-100 text-center">
              <ArrowRightLeft className="w-6 h-6 text-success mx-auto mb-2" />
              <p className="text-2xl font-bold text-success">{stats.marketplace.converted_leads}</p>
              <p className="text-xs text-muted-foreground">{t('admin.converted')}</p>
            </Card>
            
            <Card className="p-4 bg-white border border-orange-100 text-center">
              <TrendingUp className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-2xl font-bold text-primary">{stats.marketplace.conversion_rate}%</p>
              <p className="text-xs text-muted-foreground">{t('admin.conversion_rate')}</p>
            </Card>
          </div>
        </div>
      )}

      {/* User Breakdown */}
      <div>
        <h2 className="font-heading text-xl font-bold text-secondary mb-4">
          {t('admin.subscription_breakdown')}
        </h2>
        <Card className="p-6 bg-white border border-orange-100">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div className="text-center">
              <p className="text-4xl font-bold text-secondary">{stats.users.total}</p>
              <p className="text-sm text-muted-foreground">{t('admin.total_users')}</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-primary">{stats.users.premium}</p>
              <p className="text-sm text-muted-foreground">{t('admin.premium')}</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-warning">{stats.users.trial}</p>
              <p className="text-sm text-muted-foreground">{t('admin.trial')}</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-success">{stats.users.partner}</p>
              <p className="text-sm text-muted-foreground">{t('admin.partner')}</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-muted-foreground">{stats.users.free}</p>
              <p className="text-sm text-muted-foreground">{t('admin.free')}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Retention Metrics */}
      <div>
        <h2 className="font-heading text-xl font-bold text-secondary mb-4">
          {t('admin.retention_metrics')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-6 bg-white border border-orange-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('admin.retention')}</p>
                <p className="text-4xl font-bold text-secondary">{stats.retention.seven_day}%</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('admin.users_return_7_days')}
                </p>
              </div>
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-primary" />
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-white border border-orange-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('admin.retention_30')}</p>
                <p className="text-4xl font-bold text-secondary">{stats.retention.thirty_day}%</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('admin.users_return_30_days')}
                </p>
              </div>
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center">
                <Calendar className="w-8 h-8 text-success" />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
